import { useFormikContext } from "formik";
import React, { FC } from "react";
import { DEFAULT_WIDGET, WIDGET_TYPES } from "../../constants/widget-constants";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useLocale } from "../../contexts/localeContext";
import ColorPickerInput from "../color-picker/color-picker-input";
import Label from "../label/label";
import { WizardStepLayout } from '../wizard/wizard-summary-layout';
import CommandWidget from "./command/command-widget";
import ShortcutWidget from "./shortcut/shortcut-widget";
import QuickEnrolWidget from "./quick-enrol/quick-enrol-widget";

interface ColorStepProps {
    onNext: (e) => void;
    onBack?: (e) => void;
}

const ColorStep: FC<ColorStepProps> = ({ onNext, onBack }) => {
    const { values, setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();

    const getPreviewWidget = () => {
        switch (values.type) {
            case WIDGET_TYPES.COMMANDS_WIDGET:
                return <CommandWidget widget={values} previewMode={true} />
            case WIDGET_TYPES.SHORTCUT_WIDGET:
                return <ShortcutWidget widget={values} previewMode={true} />
            case WIDGET_TYPES.QUICK_ENROLL_WIDGET:
                return <QuickEnrolWidget widget={values} previewMode={true} />
            default:
                return null;
        }
    }

    const isForegroundColorSupported = (): boolean => {
        const WIDGET_WITH_FOREGROUND_COLOR: string[] = [WIDGET_TYPES.SHORTCUT_WIDGET];
        return WIDGET_WITH_FOREGROUND_COLOR.includes(values.type);
    }

    const resetColors = () => {
        setFieldValue("titleForegroundColor", DEFAULT_WIDGET.TITLE_FGCOLOR);
        setFieldValue("titleBackgroundColor", DEFAULT_WIDGET.TITLE_BKGCOLOR);
        setFieldValue("foregroundColor", DEFAULT_WIDGET.FOREGROUND_COLOR);
    }

    return <WizardStepLayout onBack={onBack} onNext={onNext}
        tertiaryActions={<button
            id="resetColorButton"
            className={`py-1 px-4 mr-2 rounded-full text-white bg-sidemenu-focus items-center`}
            type="button"
            onClick={resetColors}>{translate.byKey(TranslationKeys.resetDefault)}</button>}>
        <span className={`mb-2 text-portal-blue-primary-shade2 text-lg font-semibold`} >
            {translate.byKey(TranslationKeys.widgetConfiguration)}
        </span>
        <div id="titleTextColor" className="flex flex-col pb-1">
            <div className="flex flex-row justify-between">
                <Label
                    htmlFor="titleTextColor"
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.titleTextColor)}</Label>
            </div>
            <ColorPickerInput color={values.titleForegroundColor} onChange={(color: string) => setFieldValue("titleForegroundColor", color)} />
        </div>
        <div id="titleBackgroundColor" className={`flex flex-col ${isForegroundColorSupported() ? 'pb-1' : 'pb-2'}`}>
            <div className="flex flex-row justify-between">
                <Label
                    htmlFor="titleBackgroundColor"
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.titleBackgroundColor)}</Label>
            </div>
            <ColorPickerInput color={values.titleBackgroundColor} onChange={(color: string) => setFieldValue("titleBackgroundColor", color)} />
        </div>

        {isForegroundColorSupported() ?
            <div className="flex flex-col pb-2">
                <div id="backgroundColor" className="flex flex-row justify-between">
                    <Label
                        htmlFor="foregroundColor"
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.foregroundColor)}</Label>
                </div>
                <ColorPickerInput color={values.foregroundColor} onChange={(color: string) => setFieldValue('foregroundColor', color)} />
            </div> : null}

        <hr className="pb-2" />
        <Label
            htmlFor="titleBackgroundColor"
            className="text-xs text-gray-600 mb-1 font-semibold">{translate.byKey(TranslationKeys.preview)}</Label>
        {getPreviewWidget()}
    </WizardStepLayout>
}
export default ColorStep; 